import React, { useState, useEffect, useContext, useRef } from "react";
import {
    Form,
    Modal,
    Row,
    Typography,
    message,
    Col,
    Input,
    Select,
    Button,
    Tabs,
  } from "antd";

const DraggableResizableModal = ({
    visible,
    onCancel,
    children,
    title = "Session Calling Interface",
    defaultWidth = 700,
    defaultHeight = 520,
  }) => {
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [size, setSize] = useState({ width: defaultWidth, height: defaultHeight });
    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
    const modalRef = useRef(null);
  
    useEffect(() => {
      if (visible) {
        // Center the modal when it becomes visible
        const centerX = (window.innerWidth - size.width) / 2;
        const centerY = (window.innerHeight - size.height) / 2;
        setPosition({ x: centerX, y: centerY });
      }
    }, [visible, size.width, size.height]);
  
    const handleMouseDown = (e, type) => {
      e.preventDefault();
      if (type === 'drag') {
        setIsDragging(true);
        setDragStart({
          x: e.clientX - position.x,
          y: e.clientY - position.y,
        });
      } else if (type === 'resize') {
        setIsResizing(true);
        setResizeStart({
          x: e.clientX,
          y: e.clientY,
          width: size.width,
          height: size.height,
        });
      }
    };
  
    const handleMouseMove = (e) => {
      if (isDragging) {
        setPosition({
          x: e.clientX - dragStart.x,
          y: e.clientY - dragStart.y,
        });
      } else if (isResizing) {
        const newWidth = Math.max(500, resizeStart.width + (e.clientX - resizeStart.x));
        const newHeight = Math.max(350, resizeStart.height + (e.clientY - resizeStart.y));
        setSize({ width: newWidth, height: newHeight });
      }
    };
  
    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
    };
  
    useEffect(() => {
      if (isDragging || isResizing) {
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        return () => {
          document.removeEventListener('mousemove', handleMouseMove);
          document.removeEventListener('mouseup', handleMouseUp);
        };
      }
    }, [isDragging, isResizing, dragStart, resizeStart]);
  
    if (!visible) return null;
  
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1000,
          pointerEvents: 'none', // Make background non-interactive initially
        }}
      >
        <div
          ref={modalRef}
          style={{
            position: 'absolute',
            left: position.x,
            top: position.y,
            width: size.width,
            height: size.height,
            backgroundColor: '#fff',
            borderRadius: 6,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            minWidth: 500,
            minHeight: 350,
            pointerEvents: 'auto', // Only the modal itself is interactive
            border: '1px solid rgba(0, 0, 0, 0.2)', // Add border for better visibility
          }}
        >
          {/* Header - Draggable area */}
          <div
            style={{
              background: '#72808E',
              color: '#fff',
              fontWeight: 600,
              fontSize: 16,
              padding: '16px 24px',
              borderRadius: '6px 6px 0 0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'move',
              userSelect: 'none',
            }}
            onMouseDown={(e) => handleMouseDown(e, 'drag')}
          >
            <span>{title}</span>
            <Button
              type="text"
              onClick={()=>onCancel()}
              style={{
                border: 'none',
                color: '#fff',
                background: 'transparent',
                fontSize: 16,
                padding: 4,
              }}
            >
              ×
            </Button>
          </div>
  
          {/* Content area */}
          <div
            style={{
              flex: 1,
              overflow: 'auto',
              padding: 24,
            }}
          >
            {children}
          </div>
  
          {/* Resize handle */}
          <div
            style={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              width: 20,
              height: 20,
              cursor: 'se-resize',
              background: 'linear-gradient(-45deg, transparent 30%, #ccc 30%, #ccc 40%, transparent 40%, transparent 60%, #ccc 60%, #ccc 70%, transparent 70%)',
            }}
            onMouseDown={(e) => handleMouseDown(e, 'resize')}
          />
        </div>
      </div>
    );
  };

export default DraggableResizableModal;